import { createStore, applyMiddleware } from 'redux';
import { Provider } from 'react-redux';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import { getEnv } from './env.js';
import { notify } from './utils/notify';

// const demoUrl = 'https://demo-api.ima360.app/v1/api/'; //local purposegit
// const localBaseUrl = "http://3.22.165.7:8080/v1/api/"; //local purposegit
// const localBaseUrl = 'https://dev-api.ima360.app/v1/api/'; //local purposegit
var baseUrl = getEnv();
// Create an instance of Axios  

const axiosInstance = axios.create({
  baseURL: baseUrl['BASE_URL'],
  // baseURL: localBaseUrl,
  // baseURL: demoUrl,
}); let isRefreshing = false;
let refreshPromise = null; // store the refresh call promise

// Warn the user when an API call is still pending after this long, without aborting it.
const SLOW_RESPONSE_THRESHOLD_MS = 120000; // 2 minutes

axiosInstance.interceptors.request.use((config) => {
  config.metadata = {
    ...config.metadata,
    slowResponseTimer: setTimeout(() => {
      notify('warning', 'The server is taking longer than usual to respond. Please wait...', 'slow-api-response');
    }, SLOW_RESPONSE_THRESHOLD_MS),
  };
  return config;
});

const clearSlowResponseTimer = (config) => {
  clearTimeout(config?.metadata?.slowResponseTimer);
};

const refreshAccessToken = async (failedRequest) => {
  const refreshToken = secureLocalStorage.getItem('rFtk');

  // 🔒 Prevent multiple refresh calls - check and set atomically
  if (isRefreshing && refreshPromise) {
    // Wait for the ongoing refresh to finish
    return refreshPromise.then((newAccessToken) => {
      failedRequest.response.config.headers.Authorization = `Bearer ${newAccessToken}`;
      return axios(failedRequest.response.config);
    }).catch((error) => {
      // If refresh failed, reject the request
      return Promise.reject(error);
    });
  }

  // Set flag immediately to prevent race condition
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response =
        secureLocalStorage.getItem('user_type') === 'okta'
          ? await axiosInstance.post('okta/token', {
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
          })
          : await axiosInstance.post('refresh', {
            refresh_token: refreshToken,
          });

      const newAccessToken = response.data.response.access_token;
      localStorage.setItem('accessToken', newAccessToken);
      secureLocalStorage.setItem('aTk', newAccessToken);
      return newAccessToken;
    } catch (error) {
      // Reset state on error so retry can happen
      isRefreshing = false;
      refreshPromise = null;
      throw error;
    } finally {
      // Reset flag after refresh completes (success or failure)
      // This allows future 401s to trigger a new refresh
      isRefreshing = false;
    }
  })();

  // ✅ Once refresh completes, retry the failed request
  return refreshPromise.then((newAccessToken) => {
    failedRequest.response.config.headers.Authorization = `Bearer ${newAccessToken}`;
    return axios(failedRequest.response.config);
  }).catch((error) => {
    // If refresh failed, reject the request
    return Promise.reject(error);
  });
};

const EXECUTION_LOG_ENDPOINT = 'execution-log';

// Surfaces the backend execution log for `logNumber`. The handler and store are
// loaded lazily because both import modules that import this file (cycle).
const showExecutionLog = (logNumber) =>
  Promise.all([import('./components/ExecutionLogHandler'), import('./redux/store')])
    .then(([{ handleExecutionLog }, { store }]) => handleExecutionLog(store.dispatch, logNumber))
    .catch(() => { });

//  handle all error toasts
const showErrorToast = (error) => {
  // Opt-out for callers that render the rejection themselves (e.g. as field
  // errors) — pass `suppressErrorToast: true` in the request config.
  if (error.config?.suppressErrorToast) return;

  // An execution log carries the full backend detail, so it takes priority over
  // the plain error message. Skip it for the log endpoint itself to avoid a loop.
  const logNumber = error.response?.data?.log_number;
  if (logNumber && !error.config?.url?.includes(EXECUTION_LOG_ENDPOINT)) {
    showExecutionLog(logNumber);
    return;
  }

  if (error.response) {
    const data = error.response.data;

    // Multi-line messages keep their line breaks: the global toast renders text with pre-line.
    if (data?.complete_error_message) {
      notify('error', data.complete_error_message);
      return;
    }

    if (data?.error?.message) {
      notify('error', data.error.message);
      return;
    }

    // We got a response, but the body isn't in either shape above — empty body, HTML from
    // a load balancer/proxy (typical for a 502/503/504), or an unexpected JSON structure.
    if (error.response.status >= 500) {
      notify('error', `Server error (${error.response.status}). Please try again.`, 'server-error');
    }
    return;
  }

  // No error.response at all — the request never got a reply, so the shapes above never
  // apply: offline, DNS failure, CORS block or connection refused.
  if (!navigator.onLine) {
    notify('error', 'You appear to be offline. Please check your internet connection.', 'offline');
  } else {
    notify('error', 'Unable to reach the server. Please try again shortly.', 'unreachable');
  }
};

// Add a response interceptor to handle token expiration or any other response-related logic
axiosInstance.interceptors.response.use(
  (response) => {
    clearSlowResponseTimer(response.config);
    // Handle the response data or perform any modifications
    return response;
  },
  (error) => {
    clearSlowResponseTimer(error.config);
    // Check if the error is due to an expired token (e.g., 401 Unauthorized)
    if (error.response && error.response.status === 401) {
      // Trigger the token refresh logic
      return refreshAccessToken(error).catch((refreshError) => {
        // Handle the error when refreshing the token
        // If refresh fails, user needs to re-authenticate
        if (refreshError.response && refreshError.response.status === 401) {
          // Refresh token also expired - clear tokens and redirect to login
          secureLocalStorage.removeItem('rFtk');
          localStorage.removeItem('accessToken');
          // toast.error('Session expired. Please login again.');
        }
        else {
          // If the retried request fails with a standard error, show it!
          showErrorToast(refreshError);
        }
        return Promise.reject(refreshError);
      });
    }
    //  For all other errors on the FIRST attempt, show the toast
    showErrorToast(error);
    // Handle other error cases
    return Promise.reject(error);
  },
);

export default axiosInstance;